import { useEffect } from "react";
import { EmblaCarouselType } from "embla-carousel";
import { canHover } from "@/utils/functions/canHover";

/**
 * Quiet period after the last nested scroll before the rail is allowed to move again.
 *
 * The shopper does not feel this number on its own — autoplay then waits its OWN delay
 * (5s by default) before the first advance, so the rail actually stays still for roughly
 * `RESUME_DELAY_MS + autoplayDuration` after the last swipe. Long enough to page through
 * five images at a browsing pace without the rail ever moving mid-task.
 */
const RESUME_DELAY_MS = 1500;

/**
 * Pauses carousel autoplay while a NESTED scroller inside it is being used, and resumes
 * it once the shopper is done.
 *
 * `stopOnMouseEnter` covers PC — a hovering pointer pauses the rail and `mouseleave`
 * resumes it. Touch has no equivalent, and the Autoplay plugin's other stop path is
 * Embla's own `pointerDown` event, which only fires when Embla starts a drag. The
 * product-card image slider declines that drag outright (see ./nestedScroller), so the
 * rail got no signal at all and kept auto-advancing out from under a shopper mid-swipe
 * through a card's images.
 *
 * The signal is the nested `scroll` event, deliberately NOT `touchstart`: a finger
 * passing over the rail while scrolling the PAGE fires touchstart on a card and would
 * pause a tenant-configured feature for a gesture that has nothing to do with the
 * images, whereas a horizontal pan of a nested track is unambiguous engagement.
 * `scroll` does not bubble, but a non-bubbling event still runs the capture phase from
 * the window down to its target — so one capture listener on the viewport sees every
 * descendant scroller, and the card never needs to know this carousel exists. A page
 * scroll cannot reach it: that event targets the document, which the viewport is not an
 * ancestor of.
 */
const useStopAutoplayOnNestedScroll = (
  emblaApi: EmblaCarouselType | undefined,
  enabled: boolean,
) => {
  useEffect(() => {
    if (!enabled || !emblaApi) return;
    const root = emblaApi.rootNode();
    if (!root) return;

    // On PC the plugin's own stopOnMouseEnter / mouseleave pair already owns resuming.
    // Calling play() there would restart the rail while the cursor is still resting on
    // it — precisely what stopOnMouseEnter exists to prevent — so only touch, which has
    // no mouseleave to resume on, gets a resume of its own.
    const autoResume = !canHover();

    let resumeTimer = 0;
    let pointerHeld = false;
    // embla-carousel-autoplay@8 `init()` returns early when there is ≤1 snap (e.g. a PDP
    // gallery with a single image), leaving its `delay` array undefined and `destroyed`
    // falsy. A later play() then runs `delay[emblaApi.selectedScrollSnap()]` and throws
    // "Cannot read properties of undefined (reading '0')" / Safari's "undefined is not an
    // object (evaluating 'a[t.selectedScrollSnap()]')". Snaps only change through reInit,
    // which re-runs init, so a >1 snap list means the plugin is fully initialised.
    const autoplay = () =>
      emblaApi.scrollSnapList().length > 1 ? emblaApi.plugins().autoplay : undefined;

    const cancelResume = () => {
      window.clearTimeout(resumeTimer);
      resumeTimer = 0;
    };

    const armResume = () => {
      // A finger resting motionless on the image stops firing scroll events; without this
      // guard the countdown would expire and slide the rail out from under it.
      if (!autoResume || pointerHeld) return;
      cancelResume();
      resumeTimer = window.setTimeout(() => {
        resumeTimer = 0;
        autoplay()?.play();
      }, RESUME_DELAY_MS);
    };

    const onNestedScroll = (evt: Event) => {
      // The viewport itself is not "nested" — Embla translates its container rather than
      // scrolling it — but guard anyway so a future overflow change cannot misfire.
      if (evt.target === root) return;
      autoplay()?.stop();
      // Re-armed on every scroll frame, so the countdown runs from the LAST movement —
      // which includes the snap animation that continues after the finger lifts.
      armResume();
    };

    const onPointerDown = () => {
      pointerHeld = true;
      cancelResume();
    };

    const onPointerRelease = () => {
      pointerHeld = false;
      // Only meaningful if the shopper actually panned: a plain tap never stopped
      // autoplay, and re-arming for one would schedule a redundant play() on a plugin
      // that is already running.
      if (autoplay()?.isPlaying() === false) armResume();
    };

    const listen = { capture: true, passive: true } as const;
    root.addEventListener("scroll", onNestedScroll, listen);
    root.addEventListener("pointerdown", onPointerDown, listen);
    // Touch gets implicit pointer capture, so pointerup lands on the element that took
    // pointerdown and this listener always sees it. A mouse can release outside the rail
    // and leave `pointerHeld` set — harmless, because `autoResume` is false there anyway.
    root.addEventListener("pointerup", onPointerRelease, listen);
    root.addEventListener("pointercancel", onPointerRelease, listen);

    return () => {
      cancelResume();
      const stop = { capture: true } as const;
      root.removeEventListener("scroll", onNestedScroll, stop);
      root.removeEventListener("pointerdown", onPointerDown, stop);
      root.removeEventListener("pointerup", onPointerRelease, stop);
      root.removeEventListener("pointercancel", onPointerRelease, stop);
    };
  }, [emblaApi, enabled]);
};

export default useStopAutoplayOnNestedScroll;
